"use client"

import { Robot, User } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

interface ChatMessageProps {
  role: "user" | "assistant"
  content: React.ReactNode
  variant?: "blue" | "claude"
}

function ChatMessage({ role, content, variant = "blue" }: ChatMessageProps) {
  const isUser = role === "user"

  const userBubbleClass =
    variant === "claude"
      ? "bg-[#3d3a37] text-[#ece9e4] rounded-br-sm"
      : "bg-blue-600 text-white rounded-br-sm"

  const assistantBubbleClass =
    variant === "claude"
      ? "bg-[#35322f] border border-[#4a4642] rounded-bl-sm"
      : "bg-bg-surface border border-border-default rounded-bl-sm"

  const userAvatarClass =
    variant === "claude" ? "bg-[#d97757] text-white" : "bg-blue-600 text-white"

  const assistantAvatarClass =
    variant === "claude"
      ? "bg-[#d97757] text-white"
      : "bg-bg-surface border border-border-default text-fg-secondary"

  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${assistantAvatarClass}`}
        >
          {variant === "claude" ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
            </svg>
          ) : (
            <Icon as={Robot} className="w-3 h-3" />
          )}
        </div>
      )}
      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 ${
          isUser ? userBubbleClass : assistantBubbleClass
        }`}
      >
        <div
          className={`text-xs ${
            isUser
              ? variant === "claude"
                ? "text-[#ece9e4]"
                : "text-white"
              : variant === "claude"
                ? "text-[#ece9e4]"
                : "text-fg-default"
          }`}
        >
          {content}
        </div>
      </div>
      {isUser && (
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${userAvatarClass}`}
        >
          <Icon as={User} className={`w-3 h-3 ${variant === "claude" ? "text-white" : ""}`} />
        </div>
      )}
    </div>
  )
}

interface ChatWindowProps {
  title: string
  subtitle?: string
  messages: { role: "user" | "assistant"; content: React.ReactNode }[]
  variant?: "blue" | "claude"
  className?: string
  style?: React.CSSProperties
}

function ChatWindow({
  title,
  subtitle,
  messages,
  variant = "blue",
  className = "",
  style,
}: ChatWindowProps) {
  const headerClass =
    variant === "claude"
      ? "bg-[#2d2a27] border-b border-[#3d3a37]"
      : "border-b border-border-default"

  const bodyClass = variant === "claude" ? "bg-[#2d2a27]" : "bg-bg-default"

  const inputClass =
    variant === "claude"
      ? "bg-[#35322f] border border-[#4a4642]"
      : "bg-bg-surface border border-border-default"

  return (
    <div
      className={`${bodyClass} border ${variant === "claude" ? "border-[#3d3a37]" : "border-border-default"} rounded-xl overflow-hidden shadow-lg ${className}`}
      style={style}
    >
      {/* Chat Header */}
      <div className={`px-3 py-2 flex items-center gap-2 ${headerClass}`}>
        <div
          className={`w-2 h-2 rounded-full ${variant === "claude" ? "bg-[#d97757]" : "bg-green-500"}`}
        />
        <span
          className={`text-xs font-medium ${variant === "claude" ? "text-[#ece9e4]" : "text-fg-default"}`}
        >
          {title}
        </span>
        {subtitle && (
          <span
            className={`text-[10px] ${variant === "claude" ? "text-[#8b8880]" : "text-fg-secondary"}`}
          >
            {subtitle}
          </span>
        )}
      </div>

      {/* Chat Messages */}
      <div className="p-3 flex flex-col gap-3">
        {messages.map((msg, idx) => (
          <ChatMessage key={idx} role={msg.role} content={msg.content} variant={variant} />
        ))}
      </div>

      {/* Input Area */}
      <div
        className={`px-3 py-2 ${variant === "claude" ? "border-t border-[#3d3a37]" : "border-t border-border-default"}`}
      >
        <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${inputClass}`}>
          <span
            className={`text-[10px] flex-1 ${variant === "claude" ? "text-[#8b8880]" : "text-fg-secondary"}`}
          >
            Ask about any protocol...
          </span>
          <div
            className={`w-5 h-5 rounded flex items-center justify-center ${variant === "claude" ? "bg-[#d97757]" : "bg-blue-600"}`}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
            >
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  )
}

export function MCPChatDemo() {
  // Conversation 1: Blue theme - Uniswap analysis
  const conversation1 = [
    {
      role: "user" as const,
      content: "What's Uniswap's revenue trend over the past month?",
    },
    {
      role: "assistant" as const,
      content: (
        <div className="flex flex-col gap-1.5">
          <p>
            Uniswap generated <strong>$48.2M in fees</strong> over the past 30 days. Since the
            protocol directs 100% of fees to liquidity providers, protocol revenue is $0.
          </p>
          <div className="mt-1 p-2 bg-bg-default rounded text-[10px] font-mono">
            <div className="flex justify-between">
              <span className="text-fg-secondary">30d Fees:</span>
              <span className="text-green-500">$48.2M</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fg-secondary">30d Change:</span>
              <span className="text-green-500">+12.4%</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      role: "user" as const,
      content: "How does that compare to last quarter?",
    },
    {
      role: "assistant" as const,
      content: (
        <div className="flex flex-col gap-1.5">
          <p>
            Q4 fees totaled <strong>$142M</strong>, averaging $47M/month. The current 30d period is
            slightly above that average, suggesting continued growth.
          </p>
          <div className="mt-1 p-2 bg-bg-default rounded text-[10px] font-mono">
            <div className="flex justify-between">
              <span className="text-fg-secondary">Q4 Total:</span>
              <span>$142M</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fg-secondary">Q4 Monthly Avg:</span>
              <span>$47M</span>
            </div>
            <div className="flex justify-between">
              <span className="text-fg-secondary">Current vs Avg:</span>
              <span className="text-green-500">+2.5%</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      role: "user" as const,
      content: "What's driving the volume increase?",
    },
    {
      role: "assistant" as const,
      content: (
        <div className="flex flex-col gap-1.5">
          <p>
            The increase is primarily driven by <strong>Base</strong> and <strong>Arbitrum</strong>{" "}
            deployments, which now account for 35% of total volume.
          </p>
        </div>
      ),
    },
  ]

  // Conversation 2: Claude theme (dark mode) - Lending protocol comparison
  const conversation2 = [
    {
      role: "user" as const,
      content: "Compare Aave and Compound's current metrics",
    },
    {
      role: "assistant" as const,
      content: (
        <div className="flex flex-col gap-1.5">
          <p>Here&apos;s a comparison of the two leading lending protocols:</p>
          <div className="mt-1 p-2 bg-[#2d2a27] rounded text-[10px] font-mono">
            <div className="font-medium mb-1 text-[#ece9e4]">Aave v3</div>
            <div className="flex justify-between">
              <span className="text-[#8b8880]">TVL:</span>
              <span className="text-[#ece9e4]">$12.8B</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#8b8880]">30d Revenue:</span>
              <span className="text-[#ece9e4]">$8.2M</span>
            </div>
            <div className="font-medium mb-1 mt-2 text-[#ece9e4]">Compound</div>
            <div className="flex justify-between">
              <span className="text-[#8b8880]">TVL:</span>
              <span className="text-[#ece9e4]">$2.1B</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#8b8880]">30d Revenue:</span>
              <span className="text-[#ece9e4]">$1.4M</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      role: "user" as const,
      content: "Why is Aave's TVL so much higher?",
    },
    {
      role: "assistant" as const,
      content: (
        <div className="flex flex-col gap-1.5">
          <p>Several factors contribute to Aave&apos;s dominance:</p>
          <ul className="list-disc list-inside text-[10px] space-y-0.5 mt-1">
            <li>Multi-chain presence (10+ networks)</li>
            <li>GHO stablecoin driving deposits</li>
            <li>Better capital efficiency with E-mode</li>
            <li>More aggressive incentive programs</li>
          </ul>
        </div>
      ),
    },
    {
      role: "user" as const,
      content: "Show me their revenue trends over the past year",
    },
    {
      role: "assistant" as const,
      content: (
        <div className="flex flex-col gap-1.5">
          <p>
            Aave has consistently outperformed, with revenue growing <strong>+180% YoY</strong>{" "}
            while Compound grew <strong>+45%</strong>.
          </p>
        </div>
      ),
    },
  ]

  return (
    <div className="w-full">
      {/* Main container with offset styling */}
      <div
        className="relative"
        style={{
          overflow: "hidden",
          minHeight: "480px",
          ...FADE_MASK_STYLE,
        }}
      >
        {/* Content area that extends beyond container */}
        <div className="p-6 pt-2 relative" style={{ width: "calc(100% + 200px)", height: "100%" }}>
          {/* Two chat windows - Claude behind, Blue in front */}
          <div className="relative h-full" style={{ marginTop: "30px" }}>
            {/* Claude themed chat - behind, offset to the right, taller */}
            <ChatWindow
              title="Chat"
              subtitle="with Token Terminal"
              messages={conversation2}
              variant="claude"
              className="absolute"
              style={{
                width: "calc(50% + 30px)",
                left: "calc(50% - 30px)",
                top: "-30px",
              }}
            />

            {/* Blue themed chat - in front, slightly lower */}
            <ChatWindow
              title="AI Assistant"
              subtitle="Token Terminal MCP"
              messages={conversation1}
              variant="blue"
              className="relative z-10"
              style={{
                width: "50%",
                marginTop: "24px",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
